import { fetchItemDetails } from './demSkuMap';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';

export type DemStandardSalesLine = {
  item_code: string;
  description: string;
  unknown: boolean;
};

/** Full row for portal admin CRUD. */
export type DemSslLineRow = {
  id: number | null;
  template_code: string;
  line_no: number;
  type: string;
  item_code: string;
  description: string;
  unknown: boolean;
};

export type DemSslTemplateSummary = {
  template_code: string;
  customer_name: string;
  line_count: number;
};

export type ListSslTemplatesResult = {
  rows: DemSslTemplateSummary[];
  total: number;
};

const VERSION_KEY = 'standard_sales_lines_version';

async function queryStandardLineCodes(templateCode: string): Promise<string[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('standard_sales_lines')
    .select('item_code')
    .eq('template_code', templateCode);

  if (error) throw new Error(formatSupabaseError(error));

  return (data ?? [])
    .map((row) => String((row as { item_code?: string }).item_code ?? '').trim())
    .filter(Boolean);
}

export async function fetchStandardSalesLines(customerCode: string): Promise<DemStandardSalesLine[]> {
  const code = customerCode.trim();
  if (!code) return [];

  let codes = await queryStandardLineCodes(code);
  if (codes.length === 0 && code.includes('-')) {
    const mainCode = code.split('-')[0]!.trim();
    if (mainCode) {
      codes = await queryStandardLineCodes(mainCode);
    }
  }

  const uniqueCodes = [...new Set(codes)].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
  if (uniqueCodes.length === 0) return [];

  const details = await fetchItemDetails(uniqueCodes);
  return uniqueCodes.map((item_code) => {
    const detail = details[item_code];
    const description = detail?.description?.trim() || 'Unknown';
    return {
      item_code,
      description,
      unknown: description === 'Unknown',
    };
  });
}

async function stampSslVersion(): Promise<void> {
  const db = getDbSupabase('dem');
  const version = String(Date.now() / 1000);
  const { error } = await db.from('system_config').upsert(
    {
      key: VERSION_KEY,
      data: { v: version },
    },
    { onConflict: 'key' },
  );
  if (error) throw new Error(formatSupabaseError(error));
}

export async function listSslTemplates(options?: {
  search?: string;
}): Promise<ListSslTemplatesResult> {
  const search = options?.search?.trim() || null;

  const db = getDbSupabase('dem');
  const { data, error } = await db.rpc('list_dem_ssl_templates', {
    p_search: search,
    p_limit: 10000,
    p_offset: 0,
  });
  if (error) throw new Error(formatSupabaseError(error));

  const rowsRaw = (data ?? []) as Array<{
    template_code?: string;
    customer_name?: string;
    line_count?: number | string;
    total_count?: number | string;
  }>;
  const total =
    rowsRaw.length > 0 ? Number(rowsRaw[0]!.total_count ?? 0) || 0 : rowsRaw.length;

  return {
    total,
    rows: rowsRaw
      .map((row) => ({
        template_code: String(row.template_code ?? '').trim(),
        customer_name: String(row.customer_name ?? '').trim(),
        line_count: Number(row.line_count ?? 0) || 0,
      }))
      .filter((row) => row.template_code),
  };
}

export async function fetchSslLinesByTemplate(
  templateCode: string,
): Promise<DemSslLineRow[]> {
  const code = templateCode.trim();
  if (!code) return [];

  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('standard_sales_lines')
    .select('id, template_code, line_no, type, item_code')
    .eq('template_code', code)
    .order('line_no', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));

  const raw = (data ?? []) as Array<{
    id?: number;
    template_code?: string;
    line_no?: number;
    type?: string;
    item_code?: string;
  }>;

  const itemCodes = raw
    .map((row) => String(row.item_code ?? '').trim())
    .filter(Boolean);
  const details = itemCodes.length > 0 ? await fetchItemDetails(itemCodes) : {};

  return raw.map((row) => {
    const item_code = String(row.item_code ?? '').trim();
    const description = details[item_code]?.description?.trim() || 'Unknown';
    return {
      id: typeof row.id === 'number' ? row.id : null,
      template_code: String(row.template_code ?? code).trim(),
      line_no: Number(row.line_no ?? 0) || 0,
      type: String(row.type ?? 'Item').trim() || 'Item',
      item_code,
      description,
      unknown: !item_code || description === 'Unknown',
    };
  });
}

export type SslLineInput = {
  line_no: number;
  type?: string;
  item_code: string;
};

/**
 * Replace all lines for a template_code (delete + insert) and stamp version.
 */
export async function saveSslTemplateLines(
  templateCode: string,
  lines: SslLineInput[],
): Promise<void> {
  const code = templateCode.trim().toUpperCase();
  if (!code) throw new Error('Template code is required.');

  const cleaned: Array<{
    template_code: string;
    line_no: number;
    type: string;
    item_code: string;
  }> = [];
  const seenLineNos = new Set<number>();
  const seenItems = new Set<string>();

  for (const line of lines) {
    const item_code = String(line.item_code ?? '').trim().toUpperCase();
    if (!item_code) continue;
    const line_no = Number(line.line_no);
    if (!Number.isFinite(line_no) || line_no <= 0) {
      throw new Error(`Invalid line no for item ${item_code}.`);
    }
    if (seenLineNos.has(line_no)) {
      throw new Error(`Duplicate line no ${line_no}.`);
    }
    if (seenItems.has(item_code)) {
      throw new Error(`Duplicate item ${item_code}.`);
    }
    seenLineNos.add(line_no);
    seenItems.add(item_code);
    cleaned.push({
      template_code: code,
      line_no,
      type: String(line.type ?? 'Item').trim() || 'Item',
      item_code,
    });
  }

  cleaned.sort((a, b) => a.line_no - b.line_no);

  const db = getDbSupabase('dem');
  const { error: delError } = await db
    .from('standard_sales_lines')
    .delete()
    .eq('template_code', code);
  if (delError) throw new Error(formatSupabaseError(delError));

  if (cleaned.length > 0) {
    const { error: insError } = await db.from('standard_sales_lines').insert(cleaned);
    if (insError) throw new Error(formatSupabaseError(insError));
  }

  await stampSslVersion();
}

export async function deleteSslTemplate(templateCode: string): Promise<void> {
  const code = templateCode.trim();
  if (!code) throw new Error('Template code is required.');

  const db = getDbSupabase('dem');
  const { error } = await db
    .from('standard_sales_lines')
    .delete()
    .eq('template_code', code);
  if (error) throw new Error(formatSupabaseError(error));
  await stampSslVersion();
}

export function nextSslLineNo(existing: Array<{ line_no: number }>): number {
  let max = 0;
  for (const row of existing) {
    const n = Number(row.line_no) || 0;
    if (n > max) max = n;
  }
  if (max <= 0) return 10000;
  const step = 10000;
  return Math.ceil(max / step) * step + step;
}
